import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faClose } from "@fortawesome/pro-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { flapOut } from "../../../../utils/z-index";

const SearchInput = styled.input`
  font: normal normal normal 15px/18px ${(props) => props.theme.fonts.normal};
  color: ${(props) => props.theme.colors.grays1};
  letter-spacing: 0;
  border: 0;
  box-sizing: content-box;
  background: none;
  height: 1.4375em;
  margin: 0;
  display: block;
  min-width: 0;
  width: 100%;
  padding: 8.5px 0 8.5px 14px;

  &::placeholder {
    font: italic normal normal 15px/18px ${(props) => props.theme.fonts.normal};
    color: ${(props) => props.theme.colors.grays3};
  }

  &:focus {
    outline: 0;
  }
`;

const SearchContainer = styled.div<{ isFocused: boolean }>`
  font-weight: 400;
  border-radius: 24px;
  border: 1px solid
    ${({ isFocused, theme }) =>
      isFocused ? theme.colors.primary1 : theme.colors.grays4};
  font-style: italic;
  color: ${(props) => props.theme.colors.grays3};
  font-size: 15px;
  width: calc(100% - 120px);
  height: 38px;
  background-color: ${(props) => props.theme.colors.white};
  padding-right: 14px;
  position: relative;
  align-items: center;
  display: inline-flex;
  cursor: text;
  box-sizing: border-box;
  line-height: 1.4375em;
  letter-spacing: 0.00938em;

  @media (min-width: 960px) {
    width: 100%;
  }

  &:hover {
    background-color: ${({ isFocused, theme }) =>
      isFocused ? theme.colors.white : theme.colors.misc3};
    border: 1px solid
      ${({ isFocused, theme }) =>
        isFocused ? theme.colors.primary1 : theme.colors.grays3};

    svg {
      color: ${({ isFocused, theme }) =>
        isFocused ? theme.colors.primary1 : theme.colors.grays2};
    }
  }
`;

export const Hint = styled.div`
  color: ${(props) => props.theme.colors.grays1};
  font-size: 13px;
  position: absolute;
  padding: 10px;
  border: 1px solid ${(props) => props.theme.colors.grays4};
  box-shadow: 0 8px 16px #4c4c4c24;
  border-radius: ${(props) => props.theme.borderRadius.small};
  top: 65px;
  background-color: ${(props) => props.theme.colors.white};
  z-index: ${flapOut};
`;

type SearchFieldProps = {
  onSearch(value: string): void;
  onFocus?(): void;
  searchTerm: string;
};

const SearchField: React.FC<SearchFieldProps> = ({
  onSearch,
  onFocus,
  searchTerm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("top-bar");
  const [isFocused, setIsFocused] = useState(false);
  const searchLength = searchTerm.length;
  const icon = searchLength > 0 ? faClose : faMagnifyingGlass;

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const searchValue = e.target.value;

    onSearch(searchValue);
  }

  function handleFocus() {
    setIsFocused(true);
    onFocus?.();
  }

  function handleBlur() {
    setIsFocused(false);
  }

  function clear() {
    onSearch("");
  }

  return (
    <SearchContainer isFocused={isFocused}>
      <SearchInput
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={onChange}
        placeholder={t("topbar.search")}
        type="text"
        value={searchTerm}
      />
      <FontAwesomeIcon
        onClick={clear}
        style={{ cursor: "pointer" }}
        icon={icon as IconProp}
        size="sm"
        color={isFocused ? theme.colors.primary1 : theme.colors.grays3}
      />
    </SearchContainer>
  );
};

export default SearchField;
